import { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/feature/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import CardSkeleton from "@/components/base/CardSkeleton";
import FilterChips from "@/components/base/FilterChips";
import PlotCard from "@/components/cards/PlotCard";
import { plotList } from "@/mocks/plot";
import { useSeo } from "@/hooks/useSeo";

const STEP = 3;

export default function PlotPage() {
  const [act, setAct] = useState("all");
  const [visible, setVisible] = useState(STEP);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "剧情介绍 - 蜜桃成熟时3D",
    description:
      "蜜桃成熟时3D完整剧情介绍，按章节梳理从蜜桃园初见到多年后重逢的故事脉络，支持按段落筛选与逐章阅读。",
    keywords: "蜜桃成熟时3D剧情,剧情介绍,故事章节,剧情解读",
    path: "/plot",
  });

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 300);
    return () => window.clearTimeout(timer);
  }, [act]);

  const actOptions = useMemo(() => {
    const acts = Array.from(new Set(plotList.map((item) => item.act)));
    return [{ label: "全部章节", value: "all" }, ...acts.map((a) => ({ label: a, value: a }))];
  }, []);

  const filtered = useMemo(
    () => (act === "all" ? plotList : plotList.filter((item) => item.act === act)),
    [act],
  );

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return (
    <>
      <PageHeader
        eyebrow="STORY"
        title="剧情介绍"
        description="影片以章节化的方式讲述一段跨越多年的成长故事。你可以按段落快速定位，也可以从第一章开始，按顺序慢慢读过。"
        crumbs={[{ label: "剧情介绍" }]}
      >
        <FilterChips
          options={actOptions}
          value={act}
          onChange={(v) => { setAct(v); setVisible(STEP); }}
          label="段落"
        />
      </PageHeader>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <p className="mb-6 text-sm text-foreground-500">
            本章节共 <span className="font-medium text-primary-700">{filtered.length}</span> 个剧情章节
          </p>

          {loading ? (
            <CardSkeleton count={3} />
          ) : shown.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {shown.map((item) => (
                  <PlotCard key={item.id} item={item} />
                ))}
              </div>
              <div className="mt-10 flex flex-col items-center gap-3">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + STEP)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-add-line w-4 h-4 flex items-center justify-center"></i>
                    加载更多章节
                  </button>
                ) : (
                  <p className="text-sm text-foreground-400">已经看到全部章节了</p>
                )}
              </div>
            </>
          ) : (
            <EmptyState
              icon="ri-book-2-line"
              title="该段落暂无剧情内容"
              description="换一个段落筛选，或返回查看全部章节。"
              actionLabel="查看全部章节"
              onAction={() => { setAct("all"); setVisible(STEP); }}
            />
          )}
        </div>
      </section>
    </>
  );
}